// @dx utility
import type { Transaction } from "@shared/types/transaction";
import type { TransactionWithRelations } from "@shared/types/transaction";
import type { PriceType, VatRateCode, InvoicedStatus } from "@shared/types";
import type { CompanyJoin } from "@shared/types/company";
import type { EmployeeJoin } from "@shared/types/employee";
import type { PricingSide, WeightType } from "@shared/types";

/**
 * Side-resolved fields from a transaction.
 * Eliminates `side === 'load' ? tx.load_X : tx.unload_X` ternaries.
 */
interface SideFields {
  // Pricing
  price: number | null;
  currency: string;
  priceType: PriceType | null;
  vatRateCode: VatRateCode;
  incoterms: string | null;
  priceListId: number | null;

  // Commercial party (load_commercial_company for load, unload_commercial_company for unload)
  companyId: number | null;
  company?: CompanyJoin | null;

  // Physical location
  locationCompanyId: number | null;
  locationCompany?: CompanyJoin | null;

  // Date & weight
  date: string | null;
  weight: number | null;

  // Sales employee
  salesEmployeeId: number | null;
  salesEmployee?: EmployeeJoin | null;

  // Invoice status
  invoicedStatus: InvoicedStatus;
  receivedInvoicedStatus: InvoicedStatus;

  // Cross-border extra party (exporter for load, importer for unload)
  sideExtraCompanyId: number | null;
  sideExtraCompany?: CompanyJoin | null;
}

/**
 * Extract side-dependent fields from a transaction.
 *
 * Load side → load_price, load_commercial_company, load_place_company, load_date, loaded_weight, etc.
 * Unload side → unload_price, unload_commercial_company, unload_place_company, unload_date, unloaded_weight, etc.
 */
export function getSideFields(tx: Transaction | TransactionWithRelations, side: PricingSide): SideFields {
  const rel = tx as TransactionWithRelations;

  if (side === 'load') {
    return {
      price: tx.load_price,
      currency: tx.load_price_currency,
      priceType: tx.load_price_type,
      vatRateCode: tx.load_price_vat_rate_code,
      incoterms: tx.load_price_incoterms,
      priceListId: tx.load_price_list_id,
      companyId: tx.load_commercial_company_id,
      company: rel.load_commercial_company,
      locationCompanyId: tx.load_place_company_id,
      locationCompany: rel.load_place_company,
      date: tx.load_date,
      weight: tx.unloaded_weight, // always use unloaded_weight — loaded_weight is deprecated
      salesEmployeeId: tx.load_sales_employee_id,
      salesEmployee: rel.load_sales_employee,
      invoicedStatus: tx.load_invoiced_status,
      receivedInvoicedStatus: tx.load_received_invoiced_status,
      sideExtraCompanyId: tx.exporter_company_id,
      sideExtraCompany: rel.exporter_company,
    };
  }

  return {
    price: tx.unload_price,
    currency: tx.unload_price_currency,
    priceType: tx.unload_price_type,
    vatRateCode: tx.unload_price_vat_rate_code,
    incoterms: tx.unload_price_incoterms,
    priceListId: tx.unload_price_list_id,
    companyId: tx.unload_commercial_company_id,
    company: rel.unload_commercial_company,
    locationCompanyId: tx.unload_place_company_id,
    locationCompany: rel.unload_place_company,
    date: tx.unload_date,
    weight: tx.unloaded_weight,
    salesEmployeeId: tx.unload_sales_employee_id,
    salesEmployee: rel.unload_sales_employee,
    invoicedStatus: tx.unload_invoiced_status,
    receivedInvoicedStatus: tx.unload_received_invoiced_status,
    sideExtraCompanyId: tx.importer_company_id,
    sideExtraCompany: rel.importer_company,
  };
}

// DB column name mapping for side-dependent fields. Canonical map lives
// in `shared/sideFields.ts` so the FE helper, the class-based services,
// and any edge-function runtime all read the same shape and stay in sync.
import { getSideColumnNames, SIDE_COLUMNS, type SideColumnNames } from "@shared/sideFields";
export { getSideColumnNames, SIDE_COLUMNS };
export type { SideColumnNames };

/** Convenience field name helpers */
export function getDateField(side: PricingSide) { return getSideColumnNames(side).date; }
export function getInvoicedStatusField(side: PricingSide) { return getSideColumnNames(side).invoicedStatus; }
export function getCompanyField(side: PricingSide) { return getSideColumnNames(side).companyId; }

/** @deprecated Always returns 'unloaded' — loaded_weight is deprecated. */
export function getWeightType(_side: PricingSide): WeightType {
  return 'unloaded';
}